import type {
  BitteAiChatProps,
  CustomToolComponentProps,
  PortfolioResponse,
} from '@bitte-ai/types';
import { Message, generateId } from 'ai';
import { useEffect, useMemo, useState } from 'react';
import { convertToUIMessages } from '../lib/chat';
import { fetchChatHistory } from '../lib/fetchChatHistory';

import { ChatInitializer, useChatConfig } from '../state/chat.state';
import { useWalletState } from '../state/wallet.state';
import { UnifiedChatContent } from './chat/ChatContent';
import { TokenList } from './tools/portfolio/TokenList';

export const BitteAiChat = ({
  wallet,
  apiUrl,
  apiKey,
  historyApiUrl,
  agentId,
  options,
  customToolComponents,
  widget,
  isWidgetChat,
  messages,
  format = 'plaintext',
}: BitteAiChatProps) => {
  const [loadedData, setLoadedData] = useState({
    agentIdLoaded: '',
    uiMessages: [] as Message[],
  });

  const [isNewChatId, setIsNewChatId] = useState<boolean>(false);
  const updateChatConfig = useChatConfig();

  const getChatIdFromQueryParam = () => {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('chatId');
  };

  const ensureChatId = () => {
    let finalChatId = '';

    const existingChatId = getChatIdFromQueryParam();

    if (!existingChatId && typeof window !== 'undefined') {
      const newChatId = generateId();

      // Update URL with the new chat ID
      const url = new URL(window.location.href);
      url.searchParams.set('chatId', newChatId);
      window.history.pushState({}, '', url.toString());

      setIsNewChatId(true);
      finalChatId = newChatId;
    } else if (existingChatId) {
      finalChatId = existingChatId;
    }

    return finalChatId;
  };

  const chatId = useMemo(
    () => (isWidgetChat ? generateId() : options?.chatId || ensureChatId()),
    [isWidgetChat, options?.chatId],
  );

  // Update chat config when parameters change
  useEffect(() => {
    updateChatConfig({
      chatId,
      apiUrl,
      apiKey,
      agentId: agentId || loadedData.agentIdLoaded,
      initialMessages: loadedData.uiMessages || messages || [],
      localAgent: options?.localAgent,
      mcpServerUrl: options?.mcpServerUrl,
      format,
    });
  }, [
    chatId,
    apiUrl,
    apiKey,
    agentId,
    loadedData.agentIdLoaded,
    loadedData.uiMessages,
    messages,
    options?.localAgent,
    options?.mcpServerUrl,
    updateChatConfig,
    format,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (chatId && historyApiUrl && !isNewChatId && !isWidgetChat) {
        const chat = await fetchChatHistory(chatId, historyApiUrl);
        if (chat) {
          const uiMessages = convertToUIMessages(chat.messages);
          setLoadedData({
            agentIdLoaded: chat.agentId,
            uiMessages: uiMessages,
          });
        }
      }
    };

    fetchData();
  }, [chatId, historyApiUrl, isNewChatId]);

  const { agentIdLoaded, uiMessages } = loadedData;

  if (!customToolComponents) {
    customToolComponents = [
      {
        name: 'get-portfolio',
        component: (props: CustomToolComponentProps) => (
          <TokenList data={props.data as PortfolioResponse} />
        ),
      },
    ];
  } else {
    // Check if user hasn't already provided a get-portfolio component
    const hasGetPortfolio = customToolComponents.some(
      (component) => component.name === 'get-portfolio',
    );

    if (!hasGetPortfolio) {
      customToolComponents = [
        ...customToolComponents,
        {
          name: 'get-portfolio',
          component: (props: CustomToolComponentProps) => (
            <TokenList data={props.data as PortfolioResponse} />
          ),
        },
      ];
    }
  }

  const updateWallet = useWalletState();

  useEffect(() => {
    if (wallet) {
      updateWallet(wallet);
    }
  }, [wallet, updateWallet]);

  const chatProps = {
    wallet,
    apiUrl,
    apiKey,
    agentId: agentId ?? agentIdLoaded,
    options,
    historyApiUrl,
    customToolComponents,
    urlChatId: chatId,
    messages: uiMessages || messages,
    widget,
    isWidgetChat,
  };

  return (
    <ChatInitializer>
      <UnifiedChatContent {...chatProps} />
    </ChatInitializer>
  );
};
